import React from 'react';

import { postWebUiApi } from '../api';
import { useWebUiGlobalContext } from '../context/global/use-global-context';

export type IWebUiStatus = {
  power: string;
  firmware: string;
  partNo: string;
  cfgName: string;
  location: string;
  serial: string;
  cfgNo: string;
  changes: string;
  uptime: string;
  mac: string;
  poe: boolean;
  ignitionStatus: string;
};

const EMPTY_STATUS: IWebUiStatus = {
  power: '',
  firmware: '',
  partNo: '',
  cfgName: '',
  location: '',
  serial: '',
  cfgNo: '',
  changes: '',
  uptime: '',
  mac: '',
  poe: false,
  ignitionStatus: 'disabled',
};

function powerText(supply: { v1: boolean; v2: boolean }): string {
  if (supply.v1 && supply.v2) return 'V1 + V2';
  if (supply.v1) return 'V1';
  if (supply.v2) return 'V2';
  return '';
}

function ignitionText(ign: { V2Active?: boolean; timer?: number }): string {
  if (!ign.V2Active) return 'disabled';
  if (ign.timer && ign.timer > 0) return `timer (${ign.timer}s)`;
  return 'active';
}

export default function WebUiStatus() {
  const { currentUser } = useWebUiGlobalContext();
  const isAdmin = currentUser?.userLevel === 0;
  const [status, setStatus] = React.useState<IWebUiStatus>(EMPTY_STATUS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  const refreshStatus = React.useCallback(async () => {
    const [id, uptime, info, supply, cfg, ign, numbers] = await Promise.all([
      postWebUiApi<{
        sn: string;
        'sw version': string;
        'mgmt mac': string;
        'part number': string;
        poe?: boolean;
      }>({ service: 'system', access: 'get', command: 'get id' }),
      postWebUiApi<{ uptime: string }>({
        service: 'system',
        access: 'get',
        command: 'get uptime',
      }),
      postWebUiApi<{ location: string }>({
        service: 'system',
        access: 'get',
        command: 'get info',
      }),
      postWebUiApi<{ v1: boolean; v2: boolean }>({
        service: 'system',
        access: 'get',
        command: 'get supply',
      }),
      postWebUiApi<{ cfgName: string }>({
        service: 'system',
        access: 'get',
        command: 'get config name',
      }),
      postWebUiApi<{ V2Active?: boolean; timer?: number }>({
        service: 'ignition',
        access: 'get',
        command: 'status',
      }),
      postWebUiApi<{ 'cfg number'?: string }>({
        service: 'system',
        access: 'get',
        command: 'devicenumbers',
      }),
    ]);

    setStatus((prev) => ({
      power: powerText(supply),
      firmware: id['sw version'] ?? '',
      partNo: id['part number'] ?? '',
      cfgName: cfg.cfgName ?? '',
      location: info.location ?? '',
      serial: id.sn ?? '',
      cfgNo: numbers['cfg number'] ?? '',
      changes: prev.changes,
      uptime: uptime.uptime ?? '',
      mac: id['mgmt mac'] ?? '',
      poe: Boolean(id.poe),
      ignitionStatus: ignitionText(ign),
    }));

    window.dispatchEvent(
      new CustomEvent<boolean>('webui:poe-available', {
        detail: Boolean(id.poe),
      }),
    );
  }, []);

  const refreshUnsavedChanges = React.useCallback(async () => {
    if (!isAdmin) {
      setHasUnsavedChanges(false);
      setStatus((prev) => ({ ...prev, changes: '' }));
      return;
    }
    const unsaved = await postWebUiApi<{ changes: boolean }>({
      service: 'system',
      access: 'get',
      command: 'has unsaved changes',
      parameters: { sessionKeepAlive: false },
    });
    setHasUnsavedChanges(Boolean(unsaved.changes));
    setStatus((prev) => ({
      ...prev,
      changes: unsaved.changes ? 'Save Changes' : 'no changes',
    }));
  }, [isAdmin]);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        await Promise.all([refreshStatus(), refreshUnsavedChanges()]);
      } catch {
        if (mounted) setStatus((prev) => ({ ...prev }));
      }
    };
    load().catch(() => {});
    const t = window.setInterval(() => {
      load().catch(() => {});
    }, 5000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, [refreshStatus, refreshUnsavedChanges]);

  const saveChanges = React.useCallback(async () => {
    const baseName = (status.cfgName || '').replace(/\.cfg$/i, '');
    const input = window.prompt(
      'Please enter the config name (1-28 chars):',
      baseName,
    );
    if (!input || !input.trim()) return;
    const cfgName = input.replace(/\.cfg$/i, '').slice(0, 255);
    await postWebUiApi({
      service: 'system',
      access: 'set',
      command: 'save config with name',
      parameters: { cfgName },
    });
    await Promise.all([refreshStatus(), refreshUnsavedChanges()]);
  }, [refreshStatus, refreshUnsavedChanges, status.cfgName]);

  return (
    <div className="absolute left-0 top-20 z-20 h-20 w-full overflow-hidden border border-black bg-webui-bg">
      <div className="h-full w-full p-2 text-xs">
        <table className="h-full w-full table-fixed">
          <tbody>
            <tr>
              <td className="font-bold">Power supply:</td>
              <td>{status.power}</td>
              <td className="font-bold">Software:</td>
              <td>{status.firmware}</td>
              <td className="font-bold">Part No.:</td>
              <td>{status.partNo}</td>
              <td className="font-bold">Config Name:</td>
              <td>{status.cfgName}</td>
            </tr>
            <tr>
              <td className="font-bold">Ignition status:</td>
              <td>{status.ignitionStatus}</td>
              <td className="font-bold">Serial number:</td>
              <td>{status.serial}</td>
              <td className="font-bold">Location:</td>
              <td>{status.location}</td>
              {isAdmin ? (
                <>
                  <td className="font-bold">Config changed:</td>
                  <td>
                    {hasUnsavedChanges ? (
                      <button
                        type="button"
                        onClick={() => {
                          saveChanges().catch(() => {});
                        }}
                        className="rounded border-2 border-red-700 bg-red-600 px-2 py-1 text-xs font-bold text-white"
                      >
                        Save Changes
                      </button>
                    ) : (
                      status.changes
                    )}
                  </td>
                </>
              ) : (
                <>
                  <td />
                  <td />
                </>
              )}
            </tr>
            <tr>
              <td className="font-bold">System uptime:</td>
              <td>{status.uptime}</td>
              <td className="font-bold">Management MAC:</td>
              <td>{status.mac}</td>
              <td className="font-bold">{status.cfgNo ? 'CFG No.:' : ''}</td>
              <td>{status.cfgNo}</td>
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
